import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { ComposedChart, Line, Bar as RBar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api, apiError } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFormDraft } from "@/lib/drafts";
import { DraftPrompt, DraftRestoredBanner } from "@/components/DraftPrompt";

const BLANK = { activity_id: "", name: "", type: "machinery", qty: 1, unit: "Nos", rate_per_day: "", days: "", status: "Planned" };

export default function Resources() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [mp, setMp] = useState(null);
  const [acts, setActs] = useState([]);
  const [meta, setMeta] = useState({ machinery: [], trades: {}, wages: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const draft = useFormDraft({
    formId: `resource:${pid}`, form, setForm, blank: BLANK, onRestore: () => setOpen(true),
  });

  const load = () => {
    api.get("/resources", { params: { project_id: pid } }).then(({ data }) => setRows(data));
    api.get(`/projects/${pid}/manpower`).then(({ data }) => setMp(data));
    api.get(`/projects/${pid}/wbs`).then(({ data }) => setActs(data.filter((a) => a.total_qty > 0)));
    api.get("/meta").then(({ data }) => setMeta(data));
  };
  useEffect(() => { load(); }, [pid]);

  const save = async () => {
    try {
      await api.post("/resources", {
        project_id: pid, ...form, activity_id: form.activity_id || null,
        qty: Number(form.qty), rate_per_day: Number(form.rate_per_day || 0), days: Number(form.days || 0),
      });
      toast.success("Resource assigned");
      setOpen(false);
      setForm(BLANK);
      draft.clear();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="resources-page">
      <PageHead title="Resource & Manpower Intelligence" subtitle="Machinery matrix, productivity-driven manpower density and daily reconciliation.">
        <Button onClick={() => setOpen(true)} data-testid="resource-new-btn"><Plus className="w-4 h-4 mr-1.5" /> Assign resource</Button>
      </PageHead>

      <DraftPrompt draft={draft} label="resource assignment" />

      {mp ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
          <Kpi label="Manhours required" value={fmtNum(mp.summary.manhours_required, 0)} testId="kpi-mh-required" />
          <Kpi label="Manhours consumed" value={fmtNum(mp.summary.manhours_consumed, 0)} tone="primary" testId="kpi-mh-consumed" />
          <Kpi label="Manhours balance" value={fmtNum(mp.summary.manhours_balance, 0)} tone="warn" />
          <Kpi label="Avg daily strength" value={fmtNum(mp.summary.avg_daily_strength, 0)} />
          <Kpi label="Total idle hours" value={fmtNum(mp.summary.total_idle_hours, 0)} tone="bad" />
        </div>
      ) : null}

      <Panel title="Daily Manpower Reconciliation — planned vs mobilised vs idle" className="mb-3" testId="manpower-chart-panel">
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={mp?.daily || []} margin={{ top: 6, right: 8, left: -16 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} stroke="hsl(var(--muted-foreground))" minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <RBar dataKey="skilled" stackId="a" name="High-skilled" fill="hsl(var(--primary))" />
              <RBar dataKey="semi_skilled" stackId="a" name="Semi-skilled" fill="#F59E0B" />
              <RBar dataKey="unskilled" stackId="a" name="Unskilled" fill="#6B7280" />
              <Line type="monotone" dataKey="planned" name="Planned" stroke="#10B981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="idle_hours" name="Idle hrs" stroke="#EF4444" strokeWidth={1.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <div className="grid xl:grid-cols-2 gap-3">
        <Panel title="Resource & machinery matrix" testId="resource-table-panel">
          <div className="overflow-x-auto max-h-[50vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Resource</th><th>Type</th><th>Activity</th><th className="text-right">Qty</th><th className="text-right">Rate/day</th><th className="text-right">Days</th><th className="text-right">Cost</th><th>Status</th><th /></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`resource-row-${r.id}`}>
                    <td>{r.name}</td>
                    <td className="text-muted-foreground">{r.type}</td>
                    <td className="text-muted-foreground max-w-[150px] truncate">{r.activity_name}</td>
                    <td className="num text-right">{r.qty}</td>
                    <td className="num text-right">{fmtNum(r.rate_per_day, 0)}</td>
                    <td className="num text-right">{r.days}</td>
                    <td className="num text-right font-medium">{fmtMoney(r.total_cost)}</td>
                    <td><Badge2 tone={r.status === "Mobilised" ? "good" : "muted"}>{r.status}</Badge2></td>
                    <td><button onClick={async () => { await api.delete(`/resources/${r.id}`); load(); }} className="text-muted-foreground hover:text-red-500" data-testid={`resource-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
                {rows.length === 0 ? <tr><td colSpan={9} className="text-muted-foreground">No resources assigned.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Daily reconciliation log" testId="manpower-table-panel">
          <div className="overflow-x-auto max-h-[50vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Date</th><th className="text-right">Planned</th><th className="text-right">Actual</th><th className="text-right">Var</th><th className="text-right">Manhours</th><th className="text-right">Idle</th><th className="text-right">Productive %</th></tr></thead>
              <tbody>
                {[...(mp?.daily || [])].reverse().map((d) => (
                  <tr key={d.date}>
                    <td className="num">{fmtDate(d.date)}</td>
                    <td className="num text-right text-muted-foreground">{d.planned}</td>
                    <td className="num text-right">{d.actual}</td>
                    <td className={`num text-right ${d.variance >= 0 ? "text-emerald-500" : "text-red-500"}`}>{d.variance}</td>
                    <td className="num text-right">{fmtNum(d.manhours, 0)}</td>
                    <td className="num text-right text-amber-500">{fmtNum(d.idle_hours, 1)}</td>
                    <td className="num text-right">{d.productive_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Panel title="Trade classification & standard wage norms" className="mt-3" testId="trades-panel">
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(meta.trades || {}).map(([level, list]) => (
            <div key={level} className="border border-border p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{level.replace("_", " ")}</span>
                <span className="num text-[12px] text-primary">₹{meta.wages?.[level]}/day</span>
              </div>
              <ul className="mt-2 space-y-1">
                {list.map((t) => <li key={t} className="text-[13px] text-muted-foreground">· {t}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign resource</DialogTitle></DialogHeader>
          <DraftRestoredBanner draft={draft} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Resource</Label>
              <Select value={form.name} onValueChange={(v) => setForm({ ...form, name: v })}>
                <SelectTrigger data-testid="resource-name-select"><SelectValue placeholder="Select machinery / tool" /></SelectTrigger>
                <SelectContent>{(meta.machinery || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Activity</Label>
              <Select value={form.activity_id} onValueChange={(v) => setForm({ ...form, activity_id: v })}>
                <SelectTrigger data-testid="resource-activity-select"><SelectValue placeholder="General" /></SelectTrigger>
                <SelectContent>{acts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            {[["qty", "Qty"], ["rate_per_day", "Rate per day (₹)"], ["days", "Days deployed"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`resource-${k}-input`} /></div>
            ))}
            <div className="space-y-1.5"><Label className="text-xs uppercase">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="resource-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Planned", "Mobilised", "Demobilised"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="resource-save-btn">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
